// Reusable analytics sections. These render the org's outcome, coordinator-
// performance, and capacity metrics; the provider dashboard composes them (the
// standalone Analytics page was folded into the dashboard).
import {
    ArrowTrendingUpIcon,
    BoltIcon,
    CheckBadgeIcon,
    ExclamationTriangleIcon,
    HandRaisedIcon,
    UserGroupIcon,
    UsersIcon,
} from '@heroicons/react/24/outline'

import { formatShortDate } from '@/provider/providerAnalytics.types'

import InfoTip from './InfoTip'
import { MOCK_ANALYTICS, MOCK_COORDINATOR_PERF, MOCK_INSIGHTS } from './mockData'

export const pct = (x: number) => `${Math.round(x * 100)}%`

// Builds the analytics CSV rows for the dashboard Export button.
export function buildAnalyticsCsv(): string {
    const fo = MOCK_INSIGHTS.familyOutcomes
    const cap = MOCK_INSIGHTS.capacity
    const pop = MOCK_INSIGHTS.population
    const perf = MOCK_COORDINATOR_PERF
    const rows: string[][] = [
        ['Group', 'Metric', 'Value'],
        ['Client Outcomes', 'Client engagement rate', pct(fo.engagementRate)],
        ['Client Outcomes', 'Roadmap progress', pct(fo.roadmapProgress)],
        ['Client Outcomes', 'Clients at risk', `${fo.atRiskCount} (${pct(fo.atRiskPct)})`],
    ]
    perf.forEach((c) => {
        rows.push(['Coordinator Performance', `${c.name} - caseload`, `${c.caseload} / ${c.capacity}`])
        rows.push(['Coordinator Performance', `${c.name} - engagement`, pct(c.engagementRate)])
        rows.push(['Coordinator Performance', `${c.name} - at risk`, String(c.atRisk)])
    })
    rows.push(['Capacity & Scale', 'Clients per coordinator', cap.familiesPerCoordinator.toFixed(1)])
    rows.push(['Capacity & Scale', 'Client self-service rate', pct(cap.selfServiceRate)])
    rows.push(['Capacity & Scale', 'Coordinator touches per client', cap.touchesPerFamily.toFixed(1)])
    rows.push(['Population Insights', 'Service access progress', pct(pop.serviceAccessProgress)])
    pop.careGaps.forEach((g) => rows.push(['Population Insights', `Care gap: ${g.label}`, String(g.count)]))
    return rows.map((r) => r.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n')
}

// 1. Client outcomes — heading + the three headline KPIs (shown to every role).
export function ClientOutcomesKpis() {
    const fo = MOCK_INSIGHTS.familyOutcomes
    return (
        <Section title='Client outcomes' subtitle='Are clients making progress?'>
            <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
                <Kpi
                    icon={UsersIcon}
                    label='Client engagement rate'
                    value={pct(fo.engagementRate)}
                    sub='completed an activity in the last 30 days'
                    info='Percentage of clients who completed at least one meaningful activity (login, roadmap step, task, saved resource, or chat) in the last 30 days.'
                />
                <Kpi
                    icon={ArrowTrendingUpIcon}
                    label='Roadmap progress'
                    value={pct(fo.roadmapProgress)}
                    sub='avg milestones completed per client'
                    info='Average percentage of recommended roadmap milestones completed, across all active clients.'
                />
                <Kpi
                    icon={ExclamationTriangleIcon}
                    label='Clients at risk'
                    value={`${fo.atRiskCount}`}
                    sub={`${pct(fo.atRiskPct)} of clients showing disengagement`}
                    tone='warn'
                    info='Clients showing signs of disengagement: no meaningful activity in 30+ days, stalled roadmap progress, or incomplete onboarding.'
                />
            </div>
        </Section>
    )
}

// The two outcome charts (weekly active + onboarding funnel) as a responsive row.
export function OutcomeChartsRow() {
    const { funnel, weeklyActiveTrend } = MOCK_ANALYTICS
    return (
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10'>
            <Card title='Weekly active clients (last 8 weeks)'>
                <BarChart
                    values={weeklyActiveTrend.map((w) => w.count)}
                    labels={weeklyActiveTrend.map((w) => formatShortDate(w.weekStart) ?? '')}
                />
            </Card>
            <Card title='Onboarding & activation'>
                <FunnelBar label='Invited' value={funnel.invited} max={funnel.invited} />
                <FunnelBar label='Registered' value={funnel.registered} max={funnel.invited} />
                <FunnelBar label='Onboarding complete' value={funnel.onboarded} max={funnel.invited} />
                <FunnelBar label='Started roadmap' value={funnel.startedRoadmap} max={funnel.invited} />
                <FunnelBar label='Completed first milestone' value={funnel.firstMilestone} max={funnel.invited} />
            </Card>
        </div>
    )
}

// 2. Coordinator performance — admin + viewer.
export function CoordinatorPerformanceSection() {
    const perf = MOCK_COORDINATOR_PERF
    return (
        <Section title='Coordinator performance' subtitle='Are staff managing caseloads effectively?'>
            <div className='bg-white border border-neutral-200 rounded-2xl overflow-hidden'>
                <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                        <thead className='bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wider'>
                            <tr>
                                <th className='px-4 py-3 text-left font-medium'>Coordinator</th>
                                <th className='px-4 py-3 text-left font-medium'>
                                    Caseload utilization
                                    <InfoTip text='Active clients assigned to the coordinator divided by their target capacity. Example: 21 assigned out of 25 capacity = 84%.' />
                                </th>
                                <th className='px-4 py-3 text-left font-medium'>
                                    Client engagement
                                    <InfoTip text="Share of this coordinator's clients who completed at least one meaningful activity (login, roadmap, homework, resource, or chat) in the last 30 days." />
                                </th>
                                <th className='px-4 py-3 text-left font-medium'>
                                    At-risk clients
                                    <InfoTip text="Clients on this coordinator's caseload showing disengagement: no activity in 30+ days, stalled roadmap, or incomplete onboarding." />
                                </th>
                            </tr>
                        </thead>
                        <tbody className='divide-y divide-neutral-100'>
                            {perf.map((c) => {
                                const util = c.capacity > 0 ? c.caseload / c.capacity : 0
                                return (
                                    <tr key={c.userId} className='hover:bg-neutral-50/50'>
                                        <td className='px-4 py-3 font-medium text-neutral-900'>{c.name}</td>
                                        <td className='px-4 py-3'>
                                            <div className='flex items-center gap-2'>
                                                <div className='w-24 h-2 bg-neutral-100 rounded-full overflow-hidden'>
                                                    <div
                                                        className='h-full bg-brand-500 rounded-full'
                                                        style={{ width: `${Math.round(util * 100)}%` }}
                                                    />
                                                </div>
                                                <span className='text-xs text-neutral-600 whitespace-nowrap'>
                                                    {c.caseload} / {c.capacity} ({pct(util)})
                                                </span>
                                            </div>
                                        </td>
                                        <td className='px-4 py-3'>
                                            <span
                                                className={`text-sm font-medium ${
                                                    c.engagementRate >= 0.7
                                                        ? 'text-green-700'
                                                        : c.engagementRate >= 0.55
                                                          ? 'text-amber-700'
                                                          : 'text-red-700'
                                                }`}
                                            >
                                                {pct(c.engagementRate)}
                                            </span>
                                        </td>
                                        <td className='px-4 py-3 text-neutral-700'>
                                            {c.atRisk} {c.atRisk === 1 ? 'client' : 'clients'}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </Section>
    )
}

// 3. Capacity & scale — admin + viewer.
export function CapacityScaleSection() {
    const cap = MOCK_INSIGHTS.capacity
    return (
        <Section title='Capacity & scale' subtitle='Supporting more clients without adding headcount'>
            <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6'>
                <Kpi
                    icon={UserGroupIcon}
                    label='Clients per coordinator'
                    value={cap.familiesPerCoordinator.toFixed(1)}
                    sub={`up from ${cap.familiesPerCoordinatorPrev.toFixed(1)} last quarter`}
                    tone='good'
                    info='Total active clients divided by total active coordinators. Tracked over time to show the organization supporting more clients per coordinator.'
                />
                <Kpi
                    icon={BoltIcon}
                    label='Client self-service rate'
                    value={pct(cap.selfServiceRate)}
                    sub='of client activity completed without coordinator help'
                    info='Percentage of client activity completed independently in the app (roadmap, resources, chat, checklist) without coordinator intervention.'
                />
                <Kpi
                    icon={HandRaisedIcon}
                    label='Coordinator touches per client'
                    value={cap.touchesPerFamily.toFixed(1)}
                    sub={`down from ${cap.touchesPerFamilyPrev.toFixed(1)} last quarter`}
                    tone='good'
                    info='Average number of coordinator interventions, reviews, or outreach actions per client in the period. Stable or falling touches alongside a growing caseload indicates better scalability.'
                />
            </div>
            <Card title='Clients per coordinator over time'>
                <BarChart
                    values={cap.familiesPerCoordinatorTrend}
                    labels={cap.familiesPerCoordinatorTrend.map((_, i) =>
                        i === cap.familiesPerCoordinatorTrend.length - 1 ? 'Now' : `W${i + 1}`
                    )}
                />
            </Card>
        </Section>
    )
}

// 4. Population insights — shown to every role at the bottom of the dashboard.
export function PopulationInsightsSection() {
    const pop = MOCK_INSIGHTS.population
    const careGapMax = Math.max(...pop.careGaps.map((x) => x.count), 1)
    return (
        <Section title='Population insights' subtitle='Where does the population need more support?'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                <Card
                    title='Top care gaps'
                    info='Most frequent unmet needs across clients, drawn from stalled or incomplete milestones and the topics clients search most in chat.'
                >
                    <ul className='space-y-3'>
                        {pop.careGaps.map((g) => (
                            <li key={g.label}>
                                <div className='flex justify-between text-sm mb-1'>
                                    <span className='text-neutral-700'>{g.label}</span>
                                    <span className='text-neutral-900 font-medium'>{g.count}</span>
                                </div>
                                <div className='w-full h-2 bg-neutral-100 rounded-full overflow-hidden'>
                                    <div
                                        className='h-full bg-brand-500 rounded-full'
                                        style={{ width: `${(g.count / careGapMax) * 100}%` }}
                                    />
                                </div>
                            </li>
                        ))}
                    </ul>
                </Card>

                <div className='space-y-6'>
                    <Card
                        title='Service access progress'
                        info='Percentage of clients who connected to a recommended service or completed a key milestone within the period.'
                    >
                        <div className='flex items-center gap-4'>
                            <CheckBadgeIcon className='w-5 h-5 text-brand-600 flex-shrink-0' />
                            <div className='flex-1'>
                                <div className='flex justify-between text-sm mb-1'>
                                    <span className='text-neutral-700'>Connected to a recommended service</span>
                                    <span className='text-neutral-900 font-medium'>{pct(pop.serviceAccessProgress)}</span>
                                </div>
                                <div className='w-full h-2.5 bg-neutral-100 rounded-full overflow-hidden'>
                                    <div
                                        className='h-full bg-brand-500 rounded-full'
                                        style={{ width: pct(pop.serviceAccessProgress) }}
                                    />
                                </div>
                            </div>
                        </div>
                    </Card>

                    <Card
                        title='Clients at risk, by need'
                        info='At-risk clients grouped by the care gap or service area driving the risk, to show where added support, staffing, or resources would help most.'
                    >
                        <ul className='divide-y divide-neutral-100'>
                            {pop.atRiskByNeed.map((n) => (
                                <li key={n.need} className='flex items-center justify-between py-2.5'>
                                    <span className='text-sm text-neutral-700'>{n.need}</span>
                                    <span className='text-sm font-medium text-neutral-900'>
                                        {n.count} {n.count === 1 ? 'client' : 'clients'}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </Card>
                </div>
            </div>
        </Section>
    )
}

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

export function Section({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
    return (
        <section className='mb-10'>
            <div className='mb-4'>
                <h2 className='text-lg font-semibold text-neutral-900'>{title}</h2>
                <p className='text-sm text-neutral-500'>{subtitle}</p>
            </div>
            {children}
        </section>
    )
}

function Card({ title, info, children }: { title: string; info?: string; children: React.ReactNode }) {
    return (
        <div className='bg-white border border-neutral-200 rounded-2xl p-6'>
            <h3 className='text-base font-semibold text-neutral-900 mb-4'>
                {title}
                {info && <InfoTip text={info} />}
            </h3>
            {children}
        </div>
    )
}

function Kpi({
    icon: Icon,
    label,
    value,
    sub,
    info,
    tone = 'default',
}: {
    icon: React.ComponentType<{ className?: string }>
    label: string
    value: string
    sub: string
    info?: string
    tone?: 'default' | 'good' | 'warn'
}) {
    const valueColor = tone === 'warn' ? 'text-amber-700' : tone === 'good' ? 'text-green-700' : 'text-neutral-900'
    return (
        <div className='bg-white rounded-xl border border-neutral-200 p-4'>
            <div className='flex items-center gap-2 mb-2'>
                <Icon className='w-4 h-4 text-brand-600 flex-shrink-0' />
                <span className='text-xs uppercase tracking-wider text-neutral-500'>{label}</span>
                {info && <InfoTip text={info} />}
            </div>
            <div className={`text-2xl font-bold ${valueColor}`}>{value}</div>
            <div className='text-xs text-neutral-500 mt-0.5'>{sub}</div>
        </div>
    )
}

function FunnelBar({ label, value, max }: { label: string; value: number; max: number }) {
    const pctVal = max > 0 ? (value / max) * 100 : 0
    return (
        <div className='mb-3 last:mb-0'>
            <div className='flex justify-between text-sm mb-1'>
                <span className='text-neutral-700'>{label}</span>
                <span className='text-neutral-900 font-medium'>
                    {value} <span className='text-neutral-400'>({Math.round(pctVal)}%)</span>
                </span>
            </div>
            <div className='w-full h-2 bg-neutral-100 rounded-full overflow-hidden'>
                <div className='h-full bg-brand-500 rounded-full' style={{ width: `${pctVal}%` }} />
            </div>
        </div>
    )
}

function BarChart({ values, labels }: { values: number[]; labels: string[] }) {
    const max = Math.max(...values, 1)
    const fmt = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1))
    const TRACK_REM = 7 // tallest bar height; absolute units avoid percentage-height collapse
    return (
        <div>
            <div className='flex items-end gap-2'>
                {values.map((v, i) => (
                    <div key={i} className='flex-1 flex flex-col items-center justify-end'>
                        <span className='text-xs font-medium text-neutral-700 mb-1'>{fmt(v)}</span>
                        <div
                            className='w-full bg-brand-500 rounded-t transition-all'
                            style={{ height: `${(v / max) * TRACK_REM}rem` }}
                        />
                    </div>
                ))}
            </div>
            <div className='flex gap-2 mt-1'>
                {labels.map((l, i) => (
                    <span key={i} className='flex-1 text-center text-xs text-neutral-500'>
                        {l}
                    </span>
                ))}
            </div>
        </div>
    )
}
